export type Addon = { id: string; name: string; price: number };
export type AddonGroup = { id: string; name: string; required: boolean; min: number; max: number | null; allow_quantity: boolean; addons: Addon[] };
export type Variant = { id: string; name: string; price: number };
export type Product = {
  id: string;
  category_id: string;
  name: string;
  description: string;
  price: number;
  available: boolean;
  dietary_tags: string[];
  allergens: string[];
  has_image: boolean;
  variants: Variant[];
  addon_groups: AddonGroup[];
};
export type Menu = { version: string | null; currency: string | null; categories: { id: string; name: string }[]; products: Product[] };
export type TableInfo = {
  restaurant: { name: string; currency: string | null };
  table: { label: string };
  online: boolean;
  channel?: 'lan';
  ordering: {
    accept_mode: 'manual' | 'auto';
    offline_policy: 'queue' | 'reject';
    allow_repeat_orders: boolean;
    max_items_per_order: number;
    max_quantity_per_item: number;
    require_customer_name: boolean;
    payment_mode: 'pay_at_counter' | 'online_optional' | 'online_required';
    welcome_message: string;
  };
  menu: Menu;
};
export type CartLine = { key: string; product_id: string; variant_id: string | null; quantity: number; addons: { id: string; quantity?: number }[]; special_instructions: string };
export type RequestStatus =
  | 'queued'
  | 'awaiting_pos'
  | 'pending_acceptance'
  | 'accepted'
  | 'rejected'
  | 'preparing'
  | 'ready'
  | 'served'
  | 'completed'
  | 'cancelled'
  | 'expired';
export type OrderRequest = {
  id: string;
  status: RequestStatus;
  status_reason: string | null;
  accepted: boolean;
  table: string | null;
  order_number: string | null;
  items: CartLine[];
  note: string;
  estimated_subtotal: number;
  confirmed_totals: { subtotal: number | null; tax: number | null; total: number } | null;
  currency: string | null;
  payment_status: string;
  rated?: boolean;
  can_rate?: boolean;
  created_at: string;
  expires_at: string;
  updated_at: string;
};
export type Quote = { source: 'restaurant' | 'estimate'; subtotal: number; tax?: number | null; total?: number | null; problems?: string[] };

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public code: string,
  ) {
    super(message);
  }
}

// The dining session is an HttpOnly cookie scoped to this table's /v1/qr/<token> path: the browser
// sends it with these same-origin requests and page scripts never see it.
async function call<T>(method: string, path: string, body?: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`/v1/qr/${path}`, {
      method,
      credentials: 'same-origin',
      headers: body === undefined ? {} : { 'Content-Type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new ApiError(0, 'You appear to be offline. Check your connection and try again.', 'network');
  }
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new ApiError(res.status, data?.error ?? 'Something went wrong. Please ask a member of staff.', data?.code ?? 'error');
  return data as T;
}

export const api = {
  table: (token: string) => call<TableInfo>('GET', encodeURIComponent(token)),
  imageUrl: (token: string, productId: string) => `/v1/qr/${encodeURIComponent(token)}/images/${encodeURIComponent(productId)}`,
  quote: (token: string, items: CartLine[]) => call<Quote>('POST', `${encodeURIComponent(token)}/quote`, { items: items.map(strip) }),
  submit: (token: string, body: { client_request_id: string; items: CartLine[]; note: string; customer_name: string; customer_phone: string; wa_updates_consent: boolean }) =>
    call<{ duplicate: boolean; request: OrderRequest }>('POST', `${encodeURIComponent(token)}/orders`, { ...body, items: body.items.map(strip) }),
  status: (token: string, id: string) => call<{ request: OrderRequest; online: boolean }>('GET', `${encodeURIComponent(token)}/orders/${encodeURIComponent(id)}`),
  mine: (token: string) => call<{ requests: OrderRequest[] }>('GET', `${encodeURIComponent(token)}/orders`),
  cancel: (token: string, id: string) => call<{ request: OrderRequest }>('POST', `${encodeURIComponent(token)}/orders/${encodeURIComponent(id)}/cancel`, {}),
  rate: (token: string, id: string, rating: number, comment: string) =>
    call<{ request: OrderRequest }>('POST', `${encodeURIComponent(token)}/orders/${encodeURIComponent(id)}/feedback`, { rating, comment }),
  paymentIntent: async (token: string, id: string) => {
    const res = await fetch(`/v1/qr/${encodeURIComponent(token)}/orders/${encodeURIComponent(id)}/payment-intent`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: '{}',
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new ApiError(res.status, data?.error ?? 'Online payment is unavailable', data?.code ?? 'error');
    return data as { provider: 'stripe' | 'razorpay'; client_secret: string | null; checkout: Record<string, unknown> | null; amount_minor: number; currency: string };
  },
};

function strip(line: CartLine) {
  return { product_id: line.product_id, variant_id: line.variant_id, quantity: line.quantity, addons: line.addons, special_instructions: line.special_instructions };
}
